import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, useDraggable, useDroppable, useSensor, useSensors,
  type Announcements, type DragEndEvent, type DragOverEvent, type DragStartEvent,
} from "@dnd-kit/core";
import {
  ArrowLeft, ArrowRight, CalendarClock, ChevronLeft, ChevronRight, Mail, MoreHorizontal, MoreVertical,
  Pencil, Phone, Plus, Search, SquareKanban, StickyNote,
} from "lucide-react";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Skeleton, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ContactFormDialog } from "@/features/contact-form";
import { OpportunityDialog, useStageRequest } from "@/features/opportunity-dialog";
import { ImportOpportunitiesButton } from "@/features/opportunity-import";
import {
  DensityToggle, FilterButtons, FiltersPanel, ListSettingsPanel, ListTabs, ManageFields, OpportunityTable, QuickChips,
} from "@/features/smart-lists";
import { StageFormDialog } from "@/features/stage-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { contactName, dialText, nextText, stageGroup, stageText } from "@/lib/contacts";
import { contactChannelBadge, languageName } from "@/lib/lead-card";
import {
  BOARD_MAX, BOARD_PAGE, boardFiltersFromParams, dropAction,
  dropHint, sourceText, visibleTags, withBoardFilters, type BoardFilters, type DropAction,
} from "@/lib/opportunities";
import {
  useContactBoard, useContacts, useCreateOpportunityList, useDeleteOpportunityList, useFirms, useOpportunityLists,
  useReorderStages, useSaveUiPrefs, useStages, useUiPrefs, useUpdateOpportunityList, type ContactListFilters,
} from "@/lib/queries";
import {
  ALL_LIST, ALL_OPPORTUNITIES, CARD_FIELD_TEXT, DEFAULT_CARD_FIELDS, DEFAULT_SORT, LISTS_PREF, fromApi,
  listApiFilters, nextListName, readLists, toApi, type CardField, type Density, type SmartList,
} from "@/lib/smart-lists";
import { movedWithinGroup, stageTint } from "@/lib/stages";
import { atLeast, type BoardCard, type BoardColumn, type StageColor, type StageRow } from "@/lib/types";
import { cn, formatDateTime, formatPhoneNumber, relativeTime } from "@/lib/utils";
import { useDebounced } from "@/lib/use-debounced";
import { DialNumberButton } from "@/softphone/dial-number";

/**
 * Opportunities — every contact as a card, in a column per stage (ADR-078).
 *
 * The contacts list as GoHighLevel's board: the same scope and filters (`GET /leads/board`), so a
 * rep's board holds the contacts their list does, and the columns are the tenant's stage catalog in
 * its order (ADR-077), never a list here. The firm picker narrows it to one client's book.
 *
 * A card dragged between two open columns moves at once (`POST /leads/{id}/stage`). Dropped on an
 * exit it asks for the outcome that puts the contact there; dragged out of one it is an admin's
 * reopen; `dropAction` holds the rule and core-api holds it again. Space picks a card up for the
 * keyboard and Enter opens it. A card opens the dialog (`?lead=`) over the board, which stays put;
 * its name opens the contact's conversation instead (ACL-632).
 *
 * An admin adds, renames and reorders stages from the column headers, with the Stages page's own
 * dialogs and routes. Deleting a stage stays on that page: its leads need somewhere to go first.
 */
export function OpportunitiesPage() {
  const { user } = useAuth();
  // A superadmin manages another tenant's stages from the Stages page, where the tenant is picked.
  const canManage = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const filters = React.useMemo(() => boardFiltersFromParams(params), [params]);
  const [search, setSearch] = React.useState(filters.q);
  const debounced = useDebounced(search, 250);
  const openLead = params.get("lead");

  const update = React.useCallback(
    (next: BoardFilters) => setParams((current) => withBoardFilters(current, next), { replace: true }),
    [setParams],
  );
  React.useEffect(() => {
    if (debounced.trim() !== filters.q) update({ ...filters, q: debounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  React.useEffect(() => {
    setSearch((current) => (current.trim() === filters.q ? current : filters.q));
  }, [filters.q]);

  /** Opens a lead's dialog, on a section when a card's icon asks for one (`?sec=notes`). */
  const setOpen = (leadId: string | null, section?: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (leadId) next.set("lead", leadId);
      else next.delete("lead");
      if (leadId && section) next.set("sec", section);
      else next.delete("sec");
      return next;
    });

  // The smart lists (`lib/smart-lists.ts`): rows of `/opportunity-lists`, the person's own and the
  // ones shared with the tenant; "All opportunities" is built in. A change to a list they may not
  // edit (the built-in one, a colleague's shared one) lasts the visit; to their own it is saved.
  const toast = useToast();
  const prefs = useUiPrefs();
  const savePrefs = useSaveUiPrefs();
  const rows = useOpportunityLists();
  const createList = useCreateOpportunityList();
  const updateList = useUpdateOpportunityList();
  const deleteList = useDeleteOpportunityList();
  const saved = React.useMemo(() => (rows.data ?? []).map(fromApi), [rows.data]);
  const [overrides, setOverrides] = React.useState<Record<string, SmartList>>({});
  const allList = overrides[ALL_LIST] ?? ALL_OPPORTUNITIES;
  const lists = React.useMemo(
    () => [allList, ...saved.map((l) => overrides[l.id] ?? l)],
    [allList, saved, overrides],
  );
  const activeId = params.get("list") ?? ALL_LIST;
  const active = lists.find((l) => l.id === activeId) ?? allList;
  const [settings, setSettings] = React.useState<{ list: SmartList; isNew: boolean } | null>(null);
  const [filtering, setFiltering] = React.useState(false);
  const failed = (what: string) => (e: unknown) =>
    toast.error(what, e instanceof ApiError ? e.message : "Please try again.");

  const openList = (id: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (id === ALL_LIST) next.delete("list");
      else next.set("list", id);
      return next;
    });
  /** A change to the open list: shown at once, and saved when it is theirs to change. */
  const changeActive = (list: SmartList) => {
    setOverrides((o) => ({ ...o, [list.id]: list }));
    if (list.id !== ALL_LIST && list.canEdit) {
      updateList.mutate(
        { id: list.id, ...toApi(list) },
        {
          onSuccess: () => setOverrides(({ [list.id]: _, ...rest }) => rest),
          onError: failed("Could not save the list"),
        },
      );
    }
  };
  /** A new list of theirs from `list`: its own copy, never shared until they share it. */
  const newFrom = (list: SmartList, name: string): SmartList => ({
    ...list, id: "new", name, shared: false, mine: true, canEdit: true, ownerName: null,
  });

  // Lists kept in `ui_prefs` before they were rows move to `/opportunity-lists` once.
  const moving = React.useRef(false);
  React.useEffect(() => {
    const legacy = readLists(prefs.data);
    if (moving.current || !rows.data || legacy.length === 0) return;
    moving.current = true;
    void Promise.all(legacy.map((l) => createList.mutateAsync(toApi(l))))
      .then(() => savePrefs.mutate({ [LISTS_PREF]: null }))
      .catch(failed("Could not move your saved lists"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.data, rows.data]);

  const sent = React.useMemo(
    () => listApiFilters(active, { firm: filters.firm, q: filters.q }, "board"),
    [active, filters.firm, filters.q],
  );
  const listSent = React.useMemo(
    () => listApiFilters(active, { firm: filters.firm, q: filters.q }, "list"),
    [active, filters.firm, filters.q],
  );
  const onBoard = active.view === "board";
  const board = useContactBoard(sent, BOARD_PAGE, onBoard);
  const firms = useFirms();
  const stages = useStages();
  const total = board.data ? board.data.columns.reduce((n, c) => n + c.total, 0) + board.data.no_stage : null;
  // The pill counts what the open list shows: the board's columns, or the table's total.
  const listCount = useContacts(listSent, 0, !onBoard);
  const count = onBoard ? total : listCount.data?.total ?? null;
  const supervisor = atLeast(user?.role, "supervisor");
  const [adding, setAdding] = React.useState(false);

  const [addingStage, setAddingStage] = React.useState(false);
  const [editingStage, setEditingStage] = React.useState<StageRow | null>(null);

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface px-6 pt-5">
      <header className="-mx-6 mb-5 flex shrink-0 items-end gap-8 border-b border-border px-6">
        <h1 className="pb-3 text-xl font-semibold text-text">Opportunities</h1>
        <nav className="flex items-end gap-6 text-sm" aria-label="Opportunities">
          <span className="border-b-2 border-accent-9 pb-3 font-medium text-accent-11">Opportunities</span>
          {canManage && (
            <Link to="/stages" className="border-b-2 border-transparent pb-3 text-text hover:text-accent-11">Pipelines</Link>
          )}
        </nav>
      </header>

      <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
        <Select value={filters.firm} onValueChange={(v) => update({ ...filters, firm: v })}>
          <SelectTrigger className="h-10 w-64 text-base" aria-label="Pipeline">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All firms</SelectItem>
            <SelectItem value="none" description="Contacts no firm tag resolves">No firm</SelectItem>
            {(firms.data ?? []).map((f) => (
              <SelectItem key={f.id} value={f.code}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {count !== null && (
          <span className="rounded-full bg-accent-3 px-3 py-1 text-sm font-medium text-accent-11">
            {count.toLocaleString()} {count === 1 ? "opportunity" : "opportunities"}
          </span>
        )}
        {onBoard && board.isFetching && !board.isPending && <Spinner className="size-3.5 text-text-muted" />}
        <div className="ml-auto flex items-center gap-2">
          <ImportOpportunitiesButton canImport={supervisor} />
          <Button variant="default" className="h-9" onClick={() => setAdding(true)}>
            <Plus />
            Add opportunity
          </Button>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More">
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setAddingStage(true)}>
                  <Plus />
                  Add stage
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/stages">
                    <Pencil />
                    Manage stages
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="mb-4 shrink-0">
        <ListTabs
          lists={lists}
          active={active.id}
          onOpen={openList}
          onAdd={(view) =>
            setSettings({
              isNew: true,
              list: newFrom(
                { ...ALL_OPPORTUNITIES, view, filters: [], sort: DEFAULT_SORT, fields: DEFAULT_CARD_FIELDS, density: "comfortable" },
                nextListName(saved),
              ),
            })
          }
          onEdit={(list) => setSettings({ list, isNew: false })}
          onCustomize={() =>
            active.canEdit
              ? setSettings({ list: active, isNew: false })
              // The built-in list, or a colleague's shared one, is not edited here: Customize starts a
              // list of their own from what is on screen.
              : setSettings({ isNew: true, list: newFrom(active, active.id === ALL_LIST ? nextListName(saved) : `${active.name} (mine)`) })
          }
        />
      </div>

      <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
        <FilterButtons
          list={active}
          onFilters={() => setFiltering(true)}
          onSort={(sort) => changeActive({ ...active, sort })}
        />
        {/* An input with an icon is `w-full` inside its own wrapper; this box is its width. */}
        <div className="ml-auto w-72">
          <Input
            className="h-10 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search opportunities"
            leading={<Search />}
            aria-label="Search opportunities"
          />
        </div>
        {onBoard && (
          <DensityToggle
            density={active.density}
            onChange={(density) => changeActive({ ...active, density })}
          />
        )}
        {onBoard && <ManageFields fields={active.fields} onChange={(fields) => changeActive({ ...active, fields })} />}
      </div>

      <div className="-mt-1 mb-4 shrink-0">
        <QuickChips filters={active.filters} onChange={(f) => changeActive({ ...active, filters: f })} />
      </div>

      {!onBoard ? (
        <OpportunityTable
          filters={listSent}
          sort={active.sort}
          onSort={(sort) => changeActive({ ...active, sort })}
          onOpen={setOpen}
        />
      ) : board.isPending ? (
        <BoardSkeleton />
      ) : board.error ? (
        <Card>
          <ErrorState
            error={board.error}
            title="Could not load the board"
            action={<Button variant="outline" onClick={() => void board.refetch()}>Try again</Button>}
          />
        </Card>
      ) : board.data.columns.length === 0 ? (
        <EmptyState icon={SquareKanban} title="No stages yet" description="An admin adds the stages contacts move through on the Stages page." />
      ) : (
        <Board
          columns={board.data.columns}
          filters={sent}
          catalog={stages.data ?? []}
          canManage={canManage}
          onOpen={setOpen}
          onEditStage={setEditingStage}
          fields={active.fields}
          density={active.density}
        />
      )}

      <ContactFormDialog open={adding} onOpenChange={setAdding} onCreated={(card) => setOpen(card.id)} />
      <FiltersPanel
        open={filtering}
        onOpenChange={setFiltering}
        filters={active.filters}
        onApply={(f) => changeActive({ ...active, filters: f })}
      />
      <ListSettingsPanel
        open={settings !== null}
        onOpenChange={(open) => !open && setSettings(null)}
        list={settings?.list ?? null}
        isNew={settings?.isNew ?? false}
        onSave={(list, isNew) => {
          if (isNew) {
            createList.mutate(toApi(list), { onSuccess: (row) => openList(row.id), onError: failed("Could not add the list") });
          } else {
            setOverrides(({ [list.id]: _, ...rest }) => rest);
            updateList.mutate({ id: list.id, ...toApi(list) }, { onError: failed("Could not save the list") });
          }
        }}
        onDuplicate={(list) =>
          createList.mutate(toApi(newFrom(list, `${list.name} (copy)`)), {
            onSuccess: (row) => openList(row.id),
            onError: failed("Could not copy the list"),
          })
        }
        onDelete={(list) =>
          deleteList.mutate(list.id, { onSuccess: () => openList(ALL_LIST), onError: failed("Could not delete the list") })
        }
      />
      <OpportunityDialog leadId={openLead} section={params.get("sec") ?? undefined} onClose={() => setOpen(null)} />
      {canManage && (
        <>
          <StageFormDialog
            open={addingStage}
            onOpenChange={setAddingStage}
            catalog={stages.data ?? []}
            tenantId={null}
            notice={
              <>
                A stage is kept once it is made: the board renames and reorders stages but never deletes one. An admin can delete
                it later from the <Link to="/stages" className="underline">Stages page</Link>, and must move its contacts to
                another stage first.
              </>
            }
          />
          <StageFormDialog
            open={editingStage !== null}
            onOpenChange={(open) => !open && setEditingStage(null)}
            stage={editingStage ?? undefined}
            catalog={stages.data ?? []}
            tenantId={null}
          />
        </>
      )}
    </div>
  );
}

// --- the board -------------------------------------------------------------------------------------

const COLLAPSED_KEY = "acl.board.collapsed";

/** Which columns this browser keeps folded: a reader's layout, not the account's. */
function useCollapsed() {
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  const toggle = React.useCallback((stage: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // Storage refused (a private window): the fold lasts for this visit.
      }
      return next;
    });
  }, []);
  return [collapsed, toggle] as const;
}

type OpenLead = (leadId: string, section?: string) => void;

/** What a card's icons do: open the dialog on a section, or change a scheduled stage's time. */
interface CardActions {
  onOpen: OpenLead;
  onReschedule: (card: BoardCard) => void;
}

function Board({
  columns, filters, catalog, canManage, onOpen, onEditStage, fields, density,
}: {
  columns: BoardColumn[];
  filters: ContactListFilters;
  catalog: readonly StageRow[];
  canManage: boolean;
  onOpen: OpenLead;
  onEditStage: (stage: StageRow) => void;
  fields: readonly CardField[];
  density: Density;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const stage = useStageRequest();
  const reorder = useReorderStages();
  const [collapsed, toggleCollapsed] = useCollapsed();
  const [dragging, setDragging] = React.useState<BoardCard | null>(null);
  const [over, setOver] = React.useState<string | null>(null);
  const sensors = useSensors(
    // A few pixels before a press becomes a drag, so a click still opens the card.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Space picks up and drops; Enter is left to open the card.
    useSensor(KeyboardSensor, { keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space"] } }),
  );

  const actionFor = (card: BoardCard | null, to: string | null): DropAction | null =>
    card && to ? dropAction(card, to, user?.role) : null;

  const onDragStart = (event: DragStartEvent) => setDragging((event.active.data.current?.card as BoardCard) ?? null);
  const onDragOver = (event: DragOverEvent) => setOver((event.over?.id as string | undefined) ?? null);
  const onDragEnd = (event: DragEndEvent) => {
    const card = event.active.data.current?.card as BoardCard | undefined;
    const to = event.over?.id as string | undefined;
    setDragging(null);
    setOver(null);
    if (card && to) stage.request(card, to);
  };
  const onDragCancel = () => {
    setDragging(null);
    setOver(null);
  };

  const label = (id: string | number | undefined) => (id ? stageText(String(id)).label : "no column");
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${cardName(active.data.current?.card as BoardCard)}.`,
    onDragOver: ({ active, over: target }) => {
      const hint = dropHint(actionFor(active.data.current?.card as BoardCard, (target?.id as string) ?? null));
      return `Over ${label(target?.id)}${hint.text ? `: ${hint.text.toLowerCase()}` : ""}.`;
    },
    onDragEnd: ({ active, over: target }) => `Dropped ${cardName(active.data.current?.card as BoardCard)} on ${label(target?.id)}.`,
    onDragCancel: ({ active }) => `${cardName(active.data.current?.card as BoardCard)} put back.`,
  };

  const actions = React.useMemo<CardActions>(
    () => ({ onOpen, onReschedule: (card) => stage.reschedule(card) }),
    [onOpen, stage],
  );

  const moveStage = async (slug: string, by: -1 | 1) => {
    const order = movedWithinGroup(catalog, slug, by);
    if (!order) return;
    try {
      await reorder.mutateAsync({ slugs: order, tenantId: null });
    } catch (e) {
      toast.error("Could not move the stage", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable: "Press Space to pick the contact up, the arrow keys to move it between stages, Space to drop it, Escape to put it back. Press Enter to open it.",
        },
      }}
    >
      <div className="-mx-6 min-h-0 flex-1 overflow-x-auto px-6 pb-4 scrollbar-subtle">
        <div className="flex h-full min-h-[24rem] gap-3.5">
          {columns.map((column) => {
            const row = catalog.find((s) => s.slug === column.stage);
            return (
              <Column
                key={column.stage}
                column={column}
                color={row?.color}
                filters={filters}
                collapsed={collapsed.has(column.stage)}
                onToggle={() => toggleCollapsed(column.stage)}
                hint={dragging && over === column.stage ? dropHint(actionFor(dragging, column.stage)) : null}
                preview={dragging ? dropHint(actionFor(dragging, column.stage)) : null}
                dragging={dragging}
                actions={actions}
                fields={fields}
                density={density}
                manage={
                  canManage && row
                    ? {
                        onEdit: () => onEditStage(row),
                        onLeft: movedWithinGroup(catalog, row.slug, -1) ? () => void moveStage(row.slug, -1) : null,
                        onRight: movedWithinGroup(catalog, row.slug, 1) ? () => void moveStage(row.slug, 1) : null,
                      }
                    : null
                }
              />
            );
          })}
        </div>
      </div>
      <DragOverlay dropAnimation={null}>
        {dragging ? <CardFace card={dragging} fields={fields} density={density} lifted /> : null}
      </DragOverlay>
      {stage.dialogs}
    </DndContext>
  );
}

function Column({
  column, color, filters, collapsed, onToggle, hint, preview, dragging, actions, manage, fields, density,
}: {
  column: BoardColumn;
  /** The stage's own tint, when it has one (ADR-078); else the group's. */
  color: StageColor | null | undefined;
  filters: ContactListFilters;
  collapsed: boolean;
  onToggle: () => void;
  hint: ReturnType<typeof dropHint> | null;
  /** While a card is carried, what a drop here would do: every column says so, not only the one under it. */
  preview: ReturnType<typeof dropHint> | null;
  dragging: BoardCard | null;
  actions: CardActions;
  manage: { onEdit: () => void; onLeft: (() => void) | null; onRight: (() => void) | null } | null;
  fields: readonly CardField[];
  density: Density;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.stage });
  const [shown, setShown] = React.useState(BOARD_PAGE);
  // A column showing more than the board's first page reads its own, longer page.
  const more = useContactBoard({ ...filters, stage: column.stage }, shown, shown > BOARD_PAGE);
  const own = more.data?.columns.find((c) => c.stage === column.stage);
  const cards = shown > BOARD_PAGE && own ? own.cards : column.cards;
  const total = column.total;
  const text = stageText(column.stage);
  const group = stageGroup(column.stage) ?? "working";
  const tint = stageTint(column.stage, color, stageGroup(column.stage));
  const dueNow = column.due_now ?? 0;
  const dnc = column.dnc ?? 0;
  const count = `${total.toLocaleString()} ${total === 1 ? "opportunity" : "opportunities"}`;

  if (collapsed) {
    return (
      <button
        ref={setNodeRef}
        type="button"
        onClick={onToggle}
        title={`${text.label}: ${count}. Show the column`}
        className={cn(
          "flex w-12 shrink-0 flex-col items-center gap-3 rounded-md py-3 text-text transition-[filter] hover:brightness-95",
          tint,
          isOver && hint?.tone === "move" && "ring-2 ring-accent-8",
        )}
      >
        <ChevronRight className="size-4 text-text-muted" aria-hidden />
        <span className="text-xs font-semibold tabular-nums">{total}</span>
        <span className="text-sm font-semibold [writing-mode:vertical-rl]">{text.label}</span>
      </button>
    );
  }

  return (
    <section
      ref={setNodeRef}
      aria-label={`${text.label}, ${count}`}
      className={cn(
        "flex h-full w-[19rem] shrink-0 flex-col rounded-md transition-shadow",
        hint?.tone === "move" && "ring-2 ring-accent-8",
        hint?.tone === "ask" && "ring-2 ring-warning-9",
        hint?.tone === "refused" && "ring-2 ring-danger-9",
      )}
    >
      <header className={cn("mb-3 flex items-start justify-between gap-2 rounded-md px-3 py-2.5 shadow-xs", tint)}>
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-text" title={text.description}>{text.label}</h2>
          <p className="mt-0.5 text-xs text-text-muted">
            <span className="tabular-nums">{count}</span>
            {group === "exit" && " · by an outcome"}
          </p>
          {(dueNow > 0 || dnc > 0) && (
            <p className="mt-1 flex flex-wrap gap-1 text-2xs">
              {dueNow > 0 && (
                <span className="rounded-full bg-surface/70 px-1.5 py-px font-medium text-accent-11" title="The dialer would call them now, on some list">
                  {dueNow.toLocaleString()} due now
                </span>
              )}
              {dnc > 0 && (
                <span className="rounded-full bg-surface/70 px-1.5 py-px font-medium text-danger-11" title="Marked do-not-call">
                  {dnc.toLocaleString()} DNC
                </span>
              )}
            </p>
          )}
          {preview?.tone && <DropBadge tone={preview.tone} />}
        </div>
        <div className="-mr-1 flex shrink-0 items-center">
          {manage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" className="hover:bg-surface/60" aria-label={`${text.label} stage`}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={manage.onEdit}>
                  <Pencil />
                  Rename stage
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={!manage.onLeft} onSelect={() => manage.onLeft?.()}>
                  <ArrowLeft />
                  Move left
                </DropdownMenuItem>
                <DropdownMenuItem disabled={!manage.onRight} onSelect={() => manage.onRight?.()}>
                  <ArrowRight />
                  Move right
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="ghost" size="icon-sm" className="hover:bg-surface/60" aria-label={`Fold ${text.label}`} title="Fold the column" onClick={onToggle}>
            <ChevronLeft />
          </Button>
        </div>
      </header>

      {hint?.text && (
        <p
          className={cn(
            "mb-2 rounded-md px-2 py-1 text-2xs",
            hint.tone === "move" && "bg-accent-3 text-accent-11",
            hint.tone === "ask" && "bg-warning-2 text-warning-11",
            hint.tone === "refused" && "bg-danger-2 text-danger-11",
          )}
        >
          {hint.text}
        </p>
      )}

      <div className="min-h-24 flex-1 space-y-2.5 overflow-y-auto pb-2 pr-1 scrollbar-subtle">
        {cards.length === 0 ? (
          dragging ? (
            <p className="rounded-md border border-dashed border-border-strong px-3 py-6 text-center text-xs text-text-subtle">Drop here</p>
          ) : null
        ) : (
          cards.map((card) => <DraggableCard key={card.id} card={card} actions={actions} fields={fields} density={density} />)
        )}
      </div>

      {(cards.length < total || more.isFetching) && (
        <footer className="border-t border-border px-1 py-2 text-xs">
          {more.isFetching ? (
            <span className="flex items-center gap-2 text-text-muted"><Spinner className="size-3" /> Loading</span>
          ) : shown < BOARD_MAX ? (
            <button type="button" className="font-medium text-accent-11 hover:underline" onClick={() => setShown((n) => Math.min(n + BOARD_PAGE, BOARD_MAX))}>
              Show more · {cards.length} of {total.toLocaleString()}
            </button>
          ) : (
            <span className="text-text-muted">
              {cards.length} of {total.toLocaleString()} here. A List view shows them all.
            </span>
          )}
        </footer>
      )}
    </section>
  );
}

function DraggableCard({
  card, actions, fields, density,
}: {
  card: BoardCard;
  actions: CardActions;
  fields: readonly CardField[];
  density: Density;
}) {
  const onOpen = actions.onOpen;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id, data: { card } });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-roledescription="draggable contact"
      aria-label={`${cardName(card)}, ${stageText(card.stage).label}`}
      onClick={() => onOpen(card.id)}
      onKeyDown={(event) => {
        listeners?.onKeyDown?.(event);
        // Enter opens the card, but not while it is being carried: the keyboard drag owns the keys.
        if (event.key === "Enter" && !isDragging) onOpen(card.id);
      }}
      className={cn(
        "cursor-grab rounded-md outline-none focus-visible:ring-2 focus-visible:ring-focus active:cursor-grabbing",
        isDragging && "opacity-40",
      )}
    >
      <CardFace card={card} fields={fields} density={density} actions={actions} />
    </div>
  );
}

/** A card as GoHighLevel draws one: the name, the picked fields as "Label: value", and the counts. */
function CardFace({
  card, fields, density, actions, lifted = false,
}: {
  card: BoardCard;
  fields: readonly CardField[];
  density: Density;
  /** Absent on the copy that follows the pointer in a drag. */
  actions?: CardActions;
  lifted?: boolean;
}) {
  const source = sourceText(card.source);
  const tags = visibleTags(card.tags);
  const none = <span className="text-text-subtle">—</span>;
  const value = (field: CardField): React.ReactNode => {
    switch (field) {
      case "source":
        return <span title={card.source}>{source.label}{source.detail && <span className="text-text-subtle"> · {source.detail}</span>}</span>;
      case "firm":
        return card.firm ? card.firm.label : <span className="text-warning-11">No firm</span>;
      case "phone":
        return <span className="font-mono text-xs">{formatPhoneNumber(card.phone)}</span>;
      case "email":
        return card.email ?? none;
      case "language":
        return languageName(card.language) ?? none;
      case "stage_since":
        return card.stage_since ? <span title={formatDateTime(card.stage_since)}>{relativeTime(card.stage_since)}</span> : none;
      case "created":
        return <span title={formatDateTime(card.created_at)}>{relativeTime(card.created_at)}</span>;
      default:
        return null;
    }
  };
  const compact = density === "compact";
  const rows = compact ? [] : fields.filter((f) => f !== "tags");
  const scheduled = stageGroup(card.stage) === "scheduled";
  const next = dialerChip(card);
  const channelBadge = contactChannelBadge(card);
  return (
    <article
      className={cn(
        "rounded-md border border-border bg-surface px-3.5 py-3 shadow-xs transition-[border-color,box-shadow] hover:border-border-strong hover:shadow-sm",
        lifted && "w-[19rem] rotate-1 shadow-lg",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="min-w-0 truncate text-[15px] text-text">
          {actions ? <ConversationLink card={card} /> : cardName(card)}
        </h3>
        {(card.dnc || channelBadge) && (
          <span className="flex shrink-0 items-center gap-1">
            {card.dnc && <Badge tone="danger" size="sm">DNC</Badge>}
            {channelBadge && (
              <Badge tone="warning" size="sm" title={channelBadge.title}>{channelBadge.text}</Badge>
            )}
          </span>
        )}
      </div>
      {next && (
        <p className="mt-1.5">
          <Badge tone={next.tone} size="sm" title={next.title}>{next.text}</Badge>
        </p>
      )}
      {rows.length > 0 && (
        <dl className="mt-3 grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-2 gap-y-2 text-sm">
          {rows.map((f) => (
            <React.Fragment key={f}>
              <dt className="font-semibold text-text">{CARD_FIELD_TEXT[f]}:</dt>
              <dd className="truncate text-text-muted">{value(f)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {!compact && fields.includes("tags") && tags.shown.length > 0 && (
        <ul className="mt-2.5 flex flex-wrap items-center gap-1" aria-label="Tags">
          {tags.shown.map((tag) => (
            <li key={tag}><Badge tone="outline" size="sm" className="max-w-28 truncate">{tag}</Badge></li>
          ))}
          {tags.more > 0 && <li className="text-2xs text-text-subtle">+{tags.more}</li>}
        </ul>
      )}
      <div className={cn("flex items-center gap-4 text-text-muted", compact ? "mt-2" : "mt-3")}>
        <IconAction
          label={`Call ${formatPhoneNumber(card.phone)}`}
          render={(icon) => (actions ? <DialNumberButton number={card.phone} name={cardName(card)} lead={card}>{icon}</DialNumberButton> : icon)}
        >
          <CountIcon icon={Phone} count={card.attempts} label={`${card.attempts} dial attempt${card.attempts === 1 ? "" : "s"}`} />
        </IconAction>
        <CountIcon icon={Mail} count={card.email ? 1 : 0} label={card.email ? card.email : "No email"} dot />
        <IconAction label="Notes" onClick={actions && (() => actions.onOpen(card.id, "notes"))}>
          <CountIcon icon={StickyNote} count={card.notes} label={`${card.notes} note${card.notes === 1 ? "" : "s"}`} />
        </IconAction>
        <IconAction
          label={scheduled ? "Change the time" : "Book or update appointment"}
          onClick={actions && (() => (scheduled ? actions.onReschedule(card) : actions.onOpen(card.id, "appointments")))}
        >
          <CountIcon icon={CalendarClock} count={scheduled ? 1 : 0} label={scheduled ? `${stageText(card.stage).label}: change the time` : "Book or update appointment"} dot />
        </IconAction>
        {card.stage_since && (
          <span className="ml-auto text-2xs" title={`In ${stageText(card.stage).label} since ${formatDateTime(card.stage_since)}`}>
            {relativeTime(card.stage_since)}
          </span>
        )}
      </div>
    </article>
  );
}

/**
 * A card icon that does something. The card itself is a drag handle and opens on a click, so the
 * icon keeps its click and its pointer-down to itself — else a press on it would open the card or
 * start a drag.
 */
function IconAction({
  label, onClick, render, children,
}: {
  label: string;
  onClick?: () => void;
  render?: (icon: React.ReactNode) => React.ReactNode;
  children: React.ReactNode;
}) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  if (render) {
    return (
      <span onPointerDown={stop} onClick={stop} onKeyDown={stop}>
        {render(children)}
      </span>
    );
  }
  if (!onClick) return <>{children}</>;
  return (
    <button
      type="button"
      aria-label={label}
      className="rounded transition-colors hover:text-accent-11 [&_svg]:hover:text-accent-11"
      onPointerDown={stop}
      onKeyDown={stop}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

/**
 * The card's name opens the contact's conversation, as GoHighLevel's does (ACL-632); the rest of the
 * card still opens the dialog. Like `IconAction`, it keeps its press, click and keys to itself.
 */
function ConversationLink({ card }: { card: BoardCard }) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <Link
      to={`/conversations/team?lead=${card.id}`}
      className="cursor-pointer underline-offset-4 hover:text-accent-11 hover:underline"
      onPointerDown={stop}
      onClick={stop}
      onKeyDown={stop}
    >
      {cardName(card)}
    </Link>
  );
}

/** What the dialer holds for a lead, as a chip: when it calls next, or why it will not. */
function dialerChip(card: BoardCard): { text: string; tone: ReturnType<typeof dialText>["tone"]; title: string } | null {
  if (!card.dial) return null;
  const state = dialText(card.dial);
  const next = nextText({ dial: card.dial, next_at: card.next_at });
  return { text: next ?? state.label, tone: state.tone, title: state.description };
}

/** A column's answer while a card is carried: what a drop on it would do. */
function DropBadge({ tone }: { tone: "move" | "ask" | "refused" }) {
  const text = { move: "Move here", ask: "Asks first", refused: "Not allowed" }[tone];
  const look = { move: "bg-success-2 text-success-11", ask: "bg-warning-2 text-warning-11", refused: "bg-danger-2 text-danger-11" }[tone];
  return <span className={cn("mt-1.5 inline-flex rounded-full px-2 py-0.5 text-2xs font-medium", look)}>{text}</span>;
}

/** An icon with GoHighLevel's blue count bubble; `dot` marks a yes without a number. */
function CountIcon({ icon: Icon, count, label, dot = false }: { icon: typeof Phone; count: number; label: string; dot?: boolean }) {
  return (
    <span className="relative inline-flex" title={label} aria-label={label}>
      <Icon className={cn("size-4", count > 0 ? "text-text" : "text-text-subtle")} aria-hidden />
      {count > 0 && (
        dot ? (
          <span className="absolute -right-1 -top-1 size-2 rounded-full bg-accent-9 ring-2 ring-surface" />
        ) : (
          <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-9 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-surface">
            {count > 99 ? "99+" : count}
          </span>
        )
      )}
    </span>
  );
}

function cardName(card: BoardCard | undefined): string {
  if (!card) return "the contact";
  return contactName(card) ?? card.phone;
}

function BoardSkeleton() {
  return (
    <div className="flex gap-3 overflow-hidden" aria-busy aria-label="Loading the board">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="w-[19rem] shrink-0 space-y-2.5">
          <Skeleton className="h-14 w-full rounded-md" />
          {Array.from({ length: 3 - (i % 2) }, (_, j) => (
            <Skeleton key={j} className="h-32 w-full rounded-md" />
          ))}
        </div>
      ))}
    </div>
  );
}
